import React, { useEffect, useId, useState } from 'react';
import { SUIT_CHARS, FACE_RANKS, isRedSuit } from '../cards/cardConstants';
import { CardPips } from '../cards/cardPips';
import { parseCssUrl, useThemeCssVar } from '../hooks/useThemeCssVar';

const VIEWBOX = '0 0 256 360';
const FACE_IMG = '/cards/img/adorio.png';

/**
 * Resolve theme back image to a stable absolute URL when possible.
 * Relative CSS urls (./assets/…) can fail inside nested SVG <image> on Safari —
 * prefer absolute /cards/ paths and HTML Image() preload for load detection.
 */
function resolveBackImageHref(raw) {
  const href = parseCssUrl(raw);
  if (!href) return null;
  // data: and absolute URLs — use as-is
  if (/^(data:|https?:)/i.test(href)) return href;
  // Root-absolute (e.g. /cards/static/media/zoo-back.xxx.svg from webpack)
  if (href.startsWith('/')) {
    try {
      return new URL(href, window.location.origin).href;
    } catch {
      return href;
    }
  }
  // Relative leftover — resolve against page, then prefer public zoo back if needed
  try {
    return new URL(href, window.location.href).href;
  } catch {
    if (/zoo-back|zoo\.svg/i.test(href)) return '/cards/img/backs/zoo.svg';
    return href;
  }
}

/**
 * Face-down back: always draw outer edge + gradient fill first.
 * Optional theme image layers on top only after HTML Image() confirms load
 * (Safari often skips SVG <image onError> and leaves blank/broken art).
 */
function CardBackArt({ uid }) {
  const gradId = `card-back-grad-${uid}`;
  const clipId = `card-back-clip-${uid}`;
  const patternId = `card-back-pat-${uid}`;
  const imageRaw = useThemeCssVar('--cards-back-image', 'none');
  const imageHref = resolveBackImageHref(imageRaw);
  const [imgState, setImgState] = useState('idle'); // idle | loading | ok | fail

  useEffect(() => {
    if (!imageHref) {
      setImgState('idle');
      return undefined;
    }
    let cancelled = false;
    setImgState('loading');
    const img = new Image();
    img.onload = () => {
      if (!cancelled) setImgState('ok');
    };
    img.onerror = () => {
      if (!cancelled) setImgState('fail');
    };
    // Safari: force decode attempt; crossOrigin not needed for same-origin / data
    img.src = imageHref;
    // If already cached, complete may already be true
    if (img.complete && img.naturalWidth > 0) {
      setImgState('ok');
    }
    return () => {
      cancelled = true;
      img.onload = null;
      img.onerror = null;
    };
  }, [imageHref]);

  const showImage = !!imageHref && imgState === 'ok';
  // Subtle pattern when image fails / missing so flat gradient isn't the only cue
  const showFallbackPattern = !showImage;

  return (
    <>
      <defs>
        <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="var(--cards-back-1, #234a82)" />
          <stop offset="45%" stopColor="var(--cards-back-2, #0f2347)" />
          <stop offset="100%" stopColor="var(--cards-back-3, #1a3568)" />
        </linearGradient>
        <clipPath id={clipId}>
          <rect x="0.5" y="0.5" width="255" height="359" rx="20" ry="20" />
        </clipPath>
        {showFallbackPattern && (
          <pattern id={patternId} width="28" height="28" patternUnits="userSpaceOnUse">
            <path
              d="M14 0L28 14 14 28 0 14Z"
              fill="none"
              stroke="var(--cards-back-border, rgba(255,255,255,0.35))"
              strokeWidth="1"
              opacity="0.35"
            />
          </pattern>
        )}
      </defs>

      {/* Base fill always present (desired flat back when image missing/unsupported) */}
      <rect
        x="0.5"
        y="0.5"
        width="255"
        height="359"
        rx="20"
        ry="20"
        fill={`url(#${gradId})`}
      />

      {showFallbackPattern && (
        <rect
          x="0.5"
          y="0.5"
          width="255"
          height="359"
          rx="20"
          ry="20"
          fill={`url(#${patternId})`}
        />
      )}

      {showImage && (
        <image
          href={imageHref}
          xlinkHref={imageHref}
          x="0"
          y="0"
          width="256"
          height="360"
          preserveAspectRatio="xMidYMid slice"
          clipPath={`url(#${clipId})`}
          // Secondary safety — Safari may never fire this; Image() preload is primary
          onError={() => setImgState('fail')}
        />
      )}

      {/* Outer edge — always drawn */}
      <rect
        x="0.5"
        y="0.5"
        width="255"
        height="359"
        rx="20"
        ry="20"
        fill="none"
        stroke="var(--cards-back-edge, #000)"
        strokeWidth="1"
      />

      {/* Inner frame — color from theme; set transparent to hide */}
      <rect
        x="10"
        y="10"
        width="236"
        height="340"
        rx="14"
        ry="14"
        fill="none"
        stroke="var(--cards-back-border, rgba(255,255,255,0.88))"
        strokeWidth="2"
      />
    </>
  );
}

function PlayingCard({
  card,
  faceDown = false,
  width = 36,
  height = 50,
  className = '',
  style,
  raised = false,
  onClick,
  onDoubleClick,
  role,
  tabIndex,
  onAnimationEnd,
}) {
  const uid = useId().replace(/:/g, '');

  const interactive = onClick || onDoubleClick;
  const commonProps = {
    style,
    onClick,
    onDoubleClick,
    onAnimationEnd,
    role: role || (interactive ? 'button' : undefined),
    tabIndex: tabIndex ?? (interactive ? 0 : undefined),
  };

  if (faceDown || !card) {
    return (
      <svg
        className={[
          'playing-card',
          'playing-card--back',
          raised ? 'playing-card--raised' : '',
          className,
        ].filter(Boolean).join(' ')}
        width={width}
        height={height}
        viewBox={VIEWBOX}
        aria-hidden={!interactive || undefined}
        {...commonProps}
      >
        <CardBackArt uid={uid} />
      </svg>
    );
  }

  const color = isRedSuit(card.suit) ? '#c41e1e' : '#1a1a1a';
  const suitChar = SUIT_CHARS[card.suit] || '?';
  const isFace = FACE_RANKS.has(card.rank);
  const cornerTransform = 'rotate(180 128 180)';

  return (
    <svg
      className={[
        'playing-card',
        'playing-card--face',
        raised ? 'playing-card--raised' : '',
        className,
      ].filter(Boolean).join(' ')}
      width={width}
      height={height}
      viewBox={VIEWBOX}
      aria-hidden={!interactive || undefined}
      {...commonProps}
    >
      <rect
        x="0.5"
        y="0.5"
        width="255"
        height="359"
        rx="20"
        ry="20"
        fill="#fff"
        stroke="#000"
        strokeWidth="1"
      />
      <text x="20" y="50" fill={color} stroke={color} fontSize="54" fontFamily="Georgia, 'Times New Roman', serif">
        {card.rank}
      </text>
      <text x="30" y="90" fill={color} stroke={color} fontSize="65" fontFamily="Georgia, serif">
        {suitChar}
      </text>
      <text
        x="20"
        y="50"
        fill={color}
        stroke={color}
        fontSize="54"
        fontFamily="Georgia, 'Times New Roman', serif"
        transform={cornerTransform}
      >
        {card.rank}
      </text>
      <text
        x="30"
        y="90"
        fill={color}
        stroke={color}
        fontSize="65"
        fontFamily="Georgia, serif"
        transform={cornerTransform}
      >
        {suitChar}
      </text>
      {isFace ? (
        <image href={FACE_IMG} x="64" y="116" width="128" height="128" />
      ) : (
        <CardPips rank={card.rank} suitChar={suitChar} color={color} />
      )}
    </svg>
  );
}

export default PlayingCard;
